import { useEffect } from 'react';
import { v4 as uuid } from 'uuid';

import { usePico } from '../pico';

export const PAYWALL_ID_STORAGE_KEY = 'paywall_id';
export const PAYWALL_TRIGGER_STORAGE_KEY = 'paywall_trigger';
export const PAYWALL_LOCATION_STORAGE_KEY = 'paywall_location';
export const PAYWALL_TYPE_STORAGE_KEY = 'paywall_type';
export const PAYWALL_FEATURE_STORAGE_KEY = 'paywall_feature';

/**
 * Paywall-specific tracking props
 * @param paywallTrigger - The trigger that displayed the paywall,
 * high cardinality identifier such as 'top-nav button', 'registration survey', etc.
 * @param paywallLocation - The location of the paywall (i.e. the page)
 * @param paywallType - The type of paywall
 * @param paywallFeature - The feature that triggered the paywall (i.e. 'registration', 'analytics_reports', etc.)
 */
export type PaywallTrackingProps = {
  paywallTrigger: string;
  paywallLocation: string;
  paywallType: 'popup' | 'page' | 'banner';
  paywallFeature: string;
};

type PaywallTrackingHookProps = PaywallTrackingProps & {
  isVisible: boolean;
};

export const usePaywallTracking = (props: PaywallTrackingHookProps) => {
  const { paywallTrigger, paywallLocation, paywallType, paywallFeature, isVisible } = props;
  const pico = usePico();

  // Track paywall displayed on mount
  useEffect(() => {
    if (!isVisible || pico === null) {
      return;
    }
    const paywallId = uuid();
    sessionStorage.setItem(PAYWALL_ID_STORAGE_KEY, paywallId);
    sessionStorage.setItem(PAYWALL_TRIGGER_STORAGE_KEY, paywallTrigger);
    sessionStorage.setItem(PAYWALL_LOCATION_STORAGE_KEY, paywallLocation);
    sessionStorage.setItem(PAYWALL_TYPE_STORAGE_KEY, paywallType);
    sessionStorage.setItem(PAYWALL_FEATURE_STORAGE_KEY, paywallFeature);
    pico.track('paywall_displayed', {
      paywall_id: paywallId,
      paywall_trigger: paywallTrigger,
      paywall_location: paywallLocation,
      paywall_type: paywallType,
      paywall_feature: paywallFeature,
    });
  }, [pico, paywallTrigger, paywallLocation, paywallType, paywallFeature, isVisible]);

  // Function to track paywall dismissed
  const trackPaywallDismissed = () => {
    const paywallId = sessionStorage.getItem(PAYWALL_ID_STORAGE_KEY) ?? 'unknown';
    sessionStorage.setItem(PAYWALL_ID_STORAGE_KEY, '');
    if (pico === null) {
      return;
    }
    pico.track('paywall_dismissed', {
      paywall_id: paywallId,
      paywall_trigger: paywallTrigger,
      paywall_location: paywallLocation,
      paywall_type: paywallType,
      paywall_feature: paywallFeature,
    });
  };

  return {
    trackPaywallDismissed,
  };
};
